import { create } from 'zustand';

import { useGoogleScope, GOOGLE_SCOPES, STORAGE_KEY } from '@/common/hooks/useGoogleScope';
import handleException from '@/common/utils/errors';

export interface CalendarEvent {
  id: string;
  title: string;
  time: string;
  startDateTime: string;
  endDateTime: string;
  isAllDay: boolean;
  meetLink?: string;
}

interface GoogleCalendarItem {
  id: string;
  summary: string;
  start: { dateTime?: string; date?: string };
  end: { dateTime?: string; date?: string };
  hangoutLink?: string;
}

interface ScheduleState {
  events: CalendarEvent[];
  loading: boolean;
  authenticated: boolean;
  checkAuthState: () => Promise<void>;
  fetchEvents: () => Promise<void>;
  authenticate: () => void;
  logout: () => void;
}

export const useScheduleStore = create<ScheduleState>((set) => {
  const { checkScope, setupAuthListener, getStoredToken, refreshGoogleToken, initiateGoogleAuth } = useGoogleScope();

  const fetchEventList = async (accessToken: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${today.toISOString()}&timeMax=${tomorrow.toISOString()}&orderBy=startTime&singleEvents=true`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      handleException('Calendar API Error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
      });

      if (response.status === 403 || response.status === 401) {
        throw new Error('REFRESH_NEEDED');
      }

      throw new Error(`Failed to fetch calendar events: ${response.statusText}`);
    }

    return response.json();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processEventList = (data: any): CalendarEvent[] => {
    if (!data.items) return [];

    return data.items
      .map((item: GoogleCalendarItem): CalendarEvent | null => {
        const startRaw = item.start.dateTime ?? item.start.date;
        const endRaw = item.end.dateTime ?? item.end.date;
        if (!startRaw || !endRaw) {
          return null;
        }

        const isAllDay = !item.start.dateTime;
        const startDate = new Date(startRaw);
        const endDate = new Date(endRaw);

        const time = isAllDay
          ? 'All day'
          : `${startDate.toLocaleTimeString()} - ${endDate.toLocaleTimeString()}`;

        return {
          id: item.id,
          title: item.summary || 'Untitled Event',
          time,
          startDateTime: startRaw,
          endDateTime: endRaw,
          isAllDay,
          meetLink: item.hangoutLink,
        };
      })
      .filter((event: CalendarEvent | null): event is CalendarEvent => event !== null);
  };

  return {
    events: [],
    loading: false,
    authenticated: false,

    checkAuthState: async () => {
      try {
        const hasScope = await checkScope(GOOGLE_SCOPES.CALENDAR);
        set({ authenticated: hasScope });

        setupAuthListener(GOOGLE_SCOPES.CALENDAR, (hasScope) => {
          set({ authenticated: hasScope });
        });
      } catch (error) {
        handleException(error);
        set({ authenticated: false });
      }
    },

    authenticate: () => {
      initiateGoogleAuth([GOOGLE_SCOPES.CALENDAR]);
    },

    logout: () => {
      localStorage.removeItem(STORAGE_KEY);
      set({ authenticated: false, events: [] });
    },

    fetchEvents: async () => {
      set({ loading: true });
      try {
        const token = getStoredToken();
        if (!token) {
          throw new Error('No token available');
        }

        try {
          const data = await fetchEventList(token.accessToken);
          const events = processEventList(data);
          set({ events });
        } catch (error) {
          if (error instanceof Error && error.message === 'REFRESH_NEEDED') {
            const newToken = await refreshGoogleToken(token.refreshToken);
            const data = await fetchEventList(newToken);
            const events = processEventList(data);
            set({ events });
          } else {
            throw error;
          }
        }
      } catch (error) {
        handleException(error);
        set({ events: [] });
      } finally {
        set({ loading: false });
      }
    },
  };
});
